'use client';

import React, { FC, useState } from 'react';
import { TypeGlobalNavFields, TypeNavGroup } from '@/core/generated-types';
import { Image } from '@/components/ui';
import { NavColumn } from '@/components/nav-v2';
import { Cart } from '../Cart/Cart';
import { useDebouncedHover } from '../hooks/useDebouncedHover';
import UserMenu from './UserMenu/UserMenu';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import classNames from 'classnames';

export const LargeNav: FC<TypeGlobalNavFields & { isScrolled: boolean }> = ({
  logo,
  groups,
  signInLink,
  linkMenuLoggedIn,
  i18n,
  isScrolled,
}) => {
  const [focusedGroup, setFocusedGroup] = useState<TypeNavGroup | null>(null);
  const translations = generateTranslationsObject(i18n?.fields);

  const { handleMouseEnter, handleMouseLeave } =
    useDebouncedHover<TypeNavGroup>({
      onShow: (group) => setFocusedGroup(group ?? focusedGroup),
      onHide: () => setFocusedGroup(null),
    });

  return (
    <div
      className={classNames(
        'sticky top-0 z-50 transition-shadow duration-300',
        {
          'shadow-lg': isScrolled,
        },
      )}
    >
      {/* Nav bar */}
      <nav
        data-testid="global-nav"
        className="pl-8 pr-4 bg-white border-b border-grey-mid-light hidden md:block"
      >
        <div className="grid grid-cols-[200px_auto_200px] mx-auto h-[80px] max-w-[1200px]">
          {/* Logo */}
          {logo && (
            <div className="w-[166px] flex items-center">
              <Image imageData={logo} alt={logo.fields.altText} />
            </div>
          )}

          {/* Navigation groups */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-14">
              {groups?.map((item) => (
                <div
                  className="cursor-pointer hover:text-indigo hover:underline text-base whitespace-nowrap py-3"
                  key={item.sys.id}
                  onMouseEnter={() => handleMouseEnter(item)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.fields.title}
                </div>
              ))}
            </div>
          </div>

          {/* User actions */}
          <div className="flex items-center gap-3 justify-end">
            <UserMenu
              signInLink={signInLink}
              linkMenuLoggedIn={linkMenuLoggedIn}
              translations={translations}
            />
            <Cart />
          </div>
        </div>
      </nav>

      {/* Focused group menu */}
      {focusedGroup?.fields.columns && (
        <div
          className="bg-white border-b border-grey-mid-light py-8 top-[81px] w-full absolute z-10  shadow-lg"
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={handleMouseLeave}
        >
          <div className="max-w-7xl mx-auto px-8">
            <div
              className="grid gap-10 justify-center"
              style={{
                gridTemplateColumns: `repeat(${Math.min(focusedGroup.fields.columns.length, 5)}, minmax(min-content, 192px))`,
              }}
            >
              {focusedGroup.fields.columns.map((column) => (
                <NavColumn
                  key={column.sys.id}
                  entryName={column.fields.entryName}
                  items={column.fields.items}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(LargeNav);
